<template>
	<div class="signature-step">

		<!-- Animation feu d'artifice -->
		<div v-if="showFireworks" class="fireworks-container">
			<div v-for="n in 50" :key="n" class="confetti" :style="confettiStyle(n)" />
		</div>

		<!-- En-tête -->
		<div class="text-center mb-4">
			<div class="sig-confirmation-icon bg-primary text-white mb-2">
				<font-awesome-icon :icon="['fal', 'file-check']" />
			</div>
			<h5 class="font-weight-bold mb-0">{{ $t('monte.contrat_cree') }}</h5>
		</div>

		<!-- Étape 1 : Initialisation (faite) -->
		<div class="sig-step-row mb-4">
			<div class="sig-step-badge bg-primary text-white">
				<font-awesome-icon :icon="['fal', 'file-invoice']" />
				<font-awesome-icon :icon="['fal', 'check-circle']" class="sig-step-check text-primary" />
			</div>
			<span class="ml-3 font-weight-bold">{{ $t('monte.step_init_contrat') }}</span>
		</div>

		<!-- Étape 2 : Signature (faite) -->
		<div class="sig-step-row mb-4">
			<div class="sig-step-badge bg-primary text-white">
				<font-awesome-icon :icon="['fal', 'signature']" />
				<font-awesome-icon :icon="['fal', 'check-circle']" class="sig-step-check text-primary" />
			</div>
			<div class="ml-3 flex-grow-1">
				<p class="font-weight-bold mb-1">{{ $t('monte.step_signature_done') }}</p>
				<button
					type="button"
					class="btn btn-link p-0"
					:disabled="downloading"
					@click.prevent="downloadContract"
				>
					<b-spinner v-if="downloading" small class="mr-1" />
					<font-awesome-icon v-else :icon="['fal', 'download']" class="mr-1" />
					{{ $t('monte.telecharger_mon_contrat') }}
				</button>
			</div>
		</div>

		<!-- Étape 3 : Facture -->
		<div v-if="loadingInvoice || firstInvoice" class="sig-step-row mb-4">
			<div
				class="sig-step-badge text-white"
				:class="invoicePaid ? 'bg-primary' : 'sig-step-badge--pending'"
			>
				<b-spinner v-if="loadingInvoice" small />
				<font-awesome-icon v-else :icon="['fal', 'file-invoice-dollar']" />
				<font-awesome-icon
					v-if="!loadingInvoice && invoicePaid"
					:icon="['fal', 'check-circle']"
					class="sig-step-check text-primary"
				/>
			</div>
			<div class="ml-3 flex-grow-1">
				<p class="font-weight-bold mb-1">
					{{ invoicePaid ? $t('monte.step_facture_reglee') : $t('monte.step_facture_a_regler') }}
				</p>
				<button
					v-if="firstInvoice"
					type="button"
					class="btn btn-link p-0"
					:disabled="downloadingInvoice"
					@click.prevent="downloadInvoice"
				>
					<b-spinner v-if="downloadingInvoice" small class="mr-1" />
					<font-awesome-icon v-else :icon="['fal', 'download']" class="mr-1" />
					{{ $t('monte.telecharger_ma_facture') }}
				</button>
			</div>
		</div>

		<!-- Action -->
		<div class="text-center mt-4">
			<slot name="actions">
				<button
					type="button"
					class="btn btn-primary"
					@click.prevent="$emit('new-contract')"
				>
					<font-awesome-icon :icon="['fal', 'plus']" class="mr-1" />
					{{ $t('monte.creer_nouveau_contrat') }}
				</button>
			</slot>
		</div>

	</div>
</template>

<script>
import ContractMixin from '@/mixins/Contract.js'
import InvoiceMixin from '@/mixins/Invoice.js'

export default {
	name: 'ConfirmationStep',
	mixins: [ContractMixin, InvoiceMixin],
	props: {
		/** Identifiant du contrat signé, pour le téléchargement du PDF. */
		contractId: {
			type: Number,
			required: true,
		},
	},
	emits: ['new-contract'],
	data() {
		return {
			downloading: false,
			downloadingInvoice: false,
			loadingInvoice: false,
			invoices: [],
			showFireworks: true,
		}
	},
	computed: {
		/** Première facture du contrat, ou null si aucune. */
		firstInvoice() {
			return this.invoices.length > 0 ? this.invoices[0] : null
		},
		/** true si la première facture est soldée (invoice_balance === 0). */
		invoicePaid() {
			return this.firstInvoice !== null && this.firstInvoice.invoice_balance === 0
		},
	},
	async mounted() {
		this.loadingInvoice = true
		try {
			const result = await this.loadInvoiceContract(this.contractId)
			this.invoices = result || []
		} catch (e) {
			console.error('ConfirmationStep::mounted loadInvoiceContract => ERROR', e)
		} finally {
			this.loadingInvoice = false
		}

		// Masquer l'animation après 4 secondes
		setTimeout(() => {
			this.showFireworks = false
		}, 8000)
	},
	methods: {
		/**
		 * Télécharge le PDF du contrat signé.
		 */
		async downloadContract() {
			this.downloading = true
			try {
				await this.printContract(this.contractId)
			} catch (e) {
				console.error('ConfirmationStep::downloadContract => ERROR', e)
				this.failureToast('monte.print_error')
			} finally {
				this.downloading = false
			}
		},
		/**
		 * Télécharge la facture en PDF (format adressé).
		 */
		async downloadInvoice() {
			if (!this.firstInvoice) return
			this.downloadingInvoice = true
			try {
				await this.getAddressedPdfInvoice([this.firstInvoice])
			} catch (e) {
				console.error('ConfirmationStep::downloadInvoice => ERROR', e)
				this.failureToast('monte.print_error')
			} finally {
				this.downloadingInvoice = false
			}
		},

		/**
		 * Génère un style aléatoire pour chaque confetti.
		 */
		confettiStyle(n) {
			const colors = ['#ff6b6b', '#feca57', '#48dbfb', '#1dd1a1', '#ff9ff3', '#54a0ff', '#5f27cd']
			const color = colors[n % colors.length]
			const left = Math.random() * 100
			const delay = Math.random() * 3
			const duration = 4 + Math.random() * 4
			const size = 6 + Math.random() * 6
			const rotation = Math.random() * 360

			return {
				left: `${left}%`,
				backgroundColor: color,
				animationDelay: `${delay}s`,
				animationDuration: `${duration}s`,
				width: `${size}px`,
				height: `${size}px`,
				transform: `rotate(${rotation}deg)`,
			}
		},
	},
}
</script>
